import React, { useEffect, useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { Box } from '@lifeomic/chroma-react/components/Box';
import { Button } from '@lifeomic/chroma-react/components/Button';
import { Modal } from '@lifeomic/chroma-react/components/Modal';
import { Text } from '@lifeomic/chroma-react/components/Text';
import { TextArea } from '@lifeomic/chroma-react/components/TextArea';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { $isCodeNode } from '@lexical/code';
import { $getRoot } from 'lexical';
import {
  $isDiffHunkNode,
  DiffHunkNode,
} from '../customNodes/DiffHunkNode/DiffHunkNode';
import { $resolveDiffHunkInPlace } from '../customNodes/DiffHunkNode/DiffHunk';
import { useImpressionEditorContext } from '../../Context';
import { SAVE_COMMAND } from './SavePlugin/SavePlugin';

const messages = defineMessages({
  pendingCount: {
    id: 'markdownEditor.diffHunkBulkActions.pendingCount',
    defaultMessage:
      '{count, plural, one {# requested edit} other {# requested edits}} pending review',
  },
  acceptAll: {
    id: 'markdownEditor.diffHunkBulkActions.acceptAll',
    defaultMessage: 'Accept all',
  },
  rejectAll: {
    id: 'markdownEditor.diffHunkBulkActions.rejectAll',
    defaultMessage: 'Reject all',
  },
  rejectModalTitle: {
    id: 'markdownEditor.diffHunkBulkActions.rejectModalTitle',
    defaultMessage: 'Why are you rejecting these suggestions?',
  },
  rejectModalPlaceholder: {
    id: 'markdownEditor.diffHunkBulkActions.rejectModalPlaceholder',
    defaultMessage:
      'Tell us what was wrong with these suggestions so we can improve Zori.',
  },
  rejectModalSubmit: {
    id: 'markdownEditor.diffHunkBulkActions.rejectModalSubmit',
    defaultMessage: 'Reject all',
  },
  rejectModalCancel: {
    id: 'markdownEditor.diffHunkBulkActions.rejectModalCancel',
    defaultMessage: 'Cancel',
  },
  emptyReviewNotice: {
    id: 'markdownEditor.diffHunkBulkActions.emptyReviewNotice',
    defaultMessage: 'No requested edits left to review',
  },
  continueEditing: {
    id: 'markdownEditor.diffHunkBulkActions.continueEditing',
    defaultMessage: 'Continue editing',
  },
});

type DiffHunkBulkActionsProps = {
  /**
   * Escape hatch when `reviewDiffDocument` is active but the editor has no
   * pending DiffHunkNodes (empty review doc, conversion miss, or a failed
   * clear after the last hunk was resolved). Persists current markdown and
   * clears the review-diff extension so the editor unlocks.
   */
  onContinueWithoutPendingHunks?: (markdown: string) => void;
};

/**
 * Renders the pending-count label and "Reject all / Accept all" controls for
 * the active diff-review session. Designed to slot into the editor's bottom
 * save bar in place of the normal Discard/Save pair whenever a
 * `reviewDiffDocument` is present, so reviewers can resolve all suggestions
 * from the same spot they'd otherwise commit/cancel a manual edit.
 *
 * Must be rendered inside a `LexicalComposer` context. Each bulk action is
 * dispatched as a single `editor.update()` so `AllHunksResolvedPlugin` fires
 * once at the end and persists the resolved markdown.
 */
export const DiffHunkBulkActions: React.FC<DiffHunkBulkActionsProps> = ({
  onContinueWithoutPendingHunks,
}) => {
  const { formatMessage } = useIntl();
  const [editor] = useLexicalComposerContext();
  const { recordSuggestionHunkDecision, activeImpression } =
    useImpressionEditorContext();
  const [pendingCount, setPendingCount] = useState(0);
  const [hasUnconvertedDiffCode, setHasUnconvertedDiffCode] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [reason, setReason] = useState('');

  useEffect(() => {
    const readCount = () => {
      editor.getEditorState().read(() => {
        const children = $getRoot().getChildren();
        setPendingCount(children.filter(node => $isDiffHunkNode(node)).length);
        setHasUnconvertedDiffCode(
          children.some(
            node => $isCodeNode(node) && node.getLanguage() === 'diff',
          ),
        );
      });
    };
    readCount();
    return editor.registerUpdateListener(() => readCount());
  }, [editor]);

  useEffect(() => {
    if (pendingCount === 0) {
      setShowRejectModal(false);
      setReason('');
    }
  }, [pendingCount]);

  const handleContinueWithoutPendingHunks = () => {
    if (!onContinueWithoutPendingHunks) {
      return;
    }
    editor.dispatchCommand(SAVE_COMMAND, async (markdown: string) => {
      onContinueWithoutPendingHunks(markdown);
    });
  };

  // Still converting ```diff code fences into hunks — avoid flashing the
  // empty-review escape hatch during that brief window.
  if (pendingCount === 0 && hasUnconvertedDiffCode) {
    return null;
  }

  if (pendingCount === 0) {
    if (!onContinueWithoutPendingHunks) {
      return null;
    }
    return (
      <>
        <Box align="center" gap={1} data-testid="diff-hunk-empty-review">
          <Text size="subbody">
            {formatMessage(messages.emptyReviewNotice)}
          </Text>
        </Box>
        <Box justify="center" gap={1}>
          <Button
            variant="contained"
            onClick={handleContinueWithoutPendingHunks}
            data-testid="diff-hunk-continue-editing"
          >
            {formatMessage(messages.continueEditing)}
          </Button>
        </Box>
      </>
    );
  }

  const applyResolveAll = (keep: 'added' | 'removed', detail?: string) => {
    const n = pendingCount;
    if (n > 0) {
      recordSuggestionHunkDecision({
        traceId: activeImpression.suggestionTraceId,
        decision: keep === 'added' ? 'accepted' : 'rejected',
        detail:
          detail ??
          (keep === 'added' ? `bulk accept (${n})` : `bulk reject (${n})`),
        count: n,
      });
    }
    editor.update(() => {
      const hunks = $getRoot()
        .getChildren()
        .filter((node): node is DiffHunkNode => $isDiffHunkNode(node));
      for (const hunk of hunks) {
        $resolveDiffHunkInPlace(hunk, keep);
      }
    });
  };

  const handleRejectSubmit = () => {
    const trimmed = reason.trim();
    if (!trimmed) {
      return;
    }
    applyResolveAll('removed', trimmed);
    setReason('');
    setShowRejectModal(false);
  };

  const handleRejectDismiss = () => {
    setReason('');
    setShowRejectModal(false);
  };

  return (
    <>
      <Modal
        isOpen={showRejectModal}
        onDismiss={handleRejectDismiss}
        title={formatMessage(messages.rejectModalTitle)}
        size={1}
        fullWidth
        actions={
          <>
            <Button
              color="negative"
              onClick={handleRejectSubmit}
              disabled={!reason.trim()}
              data-testid="diff-hunk-reject-modal-submit"
            >
              {formatMessage(messages.rejectModalSubmit)}
            </Button>
            <Button
              variant="text"
              onClick={handleRejectDismiss}
              data-testid="diff-hunk-reject-modal-cancel"
            >
              {formatMessage(messages.rejectModalCancel)}
            </Button>
          </>
        }
      >
        <TextArea
          // eslint-disable-next-line jsx-a11y/no-autofocus
          autoFocus={showRejectModal}
          aria-label={formatMessage(messages.rejectModalTitle)}
          placeholder={formatMessage(messages.rejectModalPlaceholder)}
          value={reason}
          onChange={content => setReason(content.target.value)}
          fullWidth
          rows={6}
          data-testid="diff-hunk-reject-modal-reason"
        />
      </Modal>
      <Box align="center" gap={1} data-testid="diff-hunk-bulk-actions">
        <Text size="subbody">
          {formatMessage(messages.pendingCount, { count: pendingCount })}
        </Text>
      </Box>
      <Box justify="center" gap={1}>
        <Button
          variant="text"
          color="negative"
          onClick={() => setShowRejectModal(true)}
          data-testid="diff-hunk-reject-all"
        >
          {formatMessage(messages.rejectAll)}
        </Button>
        <Button
          variant="contained"
          onClick={() => applyResolveAll('added')}
          data-testid="diff-hunk-accept-all"
        >
          {formatMessage(messages.acceptAll)}
        </Button>
      </Box>
    </>
  );
};
